<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" hasBreadcrumb>
        <template #afterTitle>
          <McButton
            variant="secondary"
            class="ms-3"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
            :disabled="isEditingConversion"
            @click="$router.push({ name: 'Master Product' })"
          />
        </template>
      </PageHeader>
    </div>

    <FormulateForm ref="formRef" v-model="formValues" :errors="formErrors" class="mc-form">
      <div class="clearfix mt-3 mb-2">
        <div class="d-flex mc-list-header">
          <h4 class="card-title flex-grow-1">
            {{ $t('master.product.info') }}
          </h4>
          <McButton
            variant="secondary"
            class="mx-2"
            :text="$t('global.action.cancel')"
            icon="icon-outline_close"
            :disabled="!isEditingProduct || isEditingConversion"
            @click="$refs['mc-modal-cancel'].show()"
          />

          <McButton
            ref="save-button-ref"
            variant="primary"
            type="submit"
            :text="$t('global.save')"
            icon="icon-outline_save"
            :disabled="!isEditingProduct || isEditingConversion"
            @click="handleAccept"
          />
        </div>
      </div>

      <b-card>
        <b-row class="g-0">
          <h4 class="card-title">
            {{ $t('master.product.title') }}
          </h4>

          <div class="d-flex w-100 gap-5 mb-3">
            <ReadOnly :label="$t('master.product.productCategory')" :value="categoryInfo.name" />
            <ReadOnly
              :label="$t('master.product.minTemperature')"
              :value="categoryInfo.minTemperature"
            />
            <ReadOnly
              :label="$t('master.product.maxTemperature')"
              :value="categoryInfo.maxTemperature"
            />
            <ReadOnly
              :label="$t('master.product.boxType')"
              :customValue="true"
            >
              <template #customValue>
                <McChipList
                  v-if="categoryInfo?.boxTypes && categoryInfo.boxTypes?.length"
                  :items="categoryInfo.boxTypes"
                  :max-visible="3"
                  itemTextField="name"
                  showAllMode="linline"
                />
                <span v-else>-</span>
              </template>
            </ReadOnly>
          </div>

          <b-col v-for="productField in productFields" :key="productField.key" sm="12" md="3">
            <FormulateInput
              v-if="productField.key === 'product_type_id'"
              v-bind="productField"
              validation-behavior="live"
              :disabled="isFieldDisabled"
              :fullObject.sync="productTypeFullObject"
              :help="productField?.validation?.includes('required')"
              @update:fullObject="handleProductTypeChange"
            />
            <FormulateInput
              v-else
              v-bind="productField"
              validation-behavior="live"
              :disabled="isFieldDisabled"
              :help="productField?.validation?.includes('required')"
            />
          </b-col>
        </b-row>
        <b-row class="g-0">
          <h4 class="card-title mt-4">
            {{ $t('master.product.basicUnit') }}
          </h4>
          <b-col v-for="uomField in uomFields" :key="uomField.key" sm="12" md="3">
            <FormulateInput
              v-bind="uomField"
              validation-behavior="live"
              :initialObject="
                uomField.initialObjectKey
                  ? { id: null, name: fetchedProduct[uomField.initialObjectKey] }
                  : undefined
              "
              :help="uomField?.validation?.includes('required')"
              :disabled="isFieldDisabled"
            >
              <template v-if="uomField.suffix" #suffix>
                <div class="formulate-input-suffix">
                  <span>{{ uomField.suffix }}</span>
                </div>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
      </b-card>
    </FormulateForm>

    <ConversionTable
      ref="conversion-table-ref"
      :isAddingproduct="isAdding"
      :productId="productId"
      :uomValues="computedUomValues"
      :isEditingProduct="isEditingProduct"
      :handleDisableEdit="handleDisableEdit"
    />

    <b-modal
      id="mc-modal-cancel"
      ref="mc-modal-cancel"
      class="mc-modal"
      :title="$t('global.action.cancel')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.text.modal.cancelChange') }}
          <strong>{{ $t('master.product.info') }}</strong
          >?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.no')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.action.yes')" @click="handleCancel" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import { mapGetters } from 'vuex'

import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import ConversionTable from './components/ConversionTable.vue'
import ReadOnly from './components/ReadOnly.vue'
import McChipList from '@/components/McChipList'

import productFields from './lib/fields/productFields'
import uomFields from './lib/fields/uomFields'
import apiErrorMappings from './lib/constants/api-error-mappings'

import * as ProductApi from '@/services/rest-api/lib/product/product'

export default {
  page: {
    title: 'Form Produk',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    ConversionTable,
    McButton,
    ReadOnly,
    McChipList,
  },
  data() {
    return {
      productFields: productFields,
      uomFields: uomFields,

      formValues: {},
      formErrors: {},
      invalidFormValues: {},
      fetchedProduct: {},
      productData: {},

      isSubmitting: false,
      isEditingConversion: false,
      isLoadingInitialData: false,

      categoryInfo: {
        name: '-',
        minTemperature: '-',
        maxTemperature: '-',
        boxTypes: null,
      },

      productTypeFullObject: null,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      return this.isAdding ? this.$t('master.product.add') : this.$t('master.product.detail')
    },
    productId() {
      return this.$route.params.id
    },
    isAdding() {
      return typeof this.$route.params.id === 'undefined'
    },
    fieldKeys() {
      const productKeys = this.productFields.reduce((obj, val) => {
        obj[val.key] = ''
        return obj
      }, {})

      const uomKeys = this.uomFields.reduce((obj, val) => {
        if (val._type === 'number') obj[val.key] = null
        else obj[val.key] = ''
        return obj
      }, {})

      return Object.assign({}, productKeys, uomKeys)
    },
    items() {
      return [
        {
          text: this.$t('menuitems.masterData.text'),
          to: '/master',
        },
        {
          text: 'Produk',
          to: '/master/product',
        },
        {
          text: this.title,
        },
      ]
    },
    computedUomValues() {
      return {
        uom: this.formValues.uom,
        weight: this.formValues.weight,
        volume: this.formValues.volume,
        price: this.formValues.price,
      }
    },
    isEditingProduct() {
      const res = this.filterUnchangedData()
      return res[1]
    },
    errorKeys() {
      return Object.keys(this.formErrors)
    },
    isFieldDisabled() {
      return (!this.isAdding && !this.fetchedProduct) || this.isEditingConversion
    },
  },
  watch: {
    formValues() {
      if (this.errorKeys.length === 0) return

      this.errorKeys.forEach((errorKey) => {
        if (this.invalidFormValues[errorKey] != this.formValues[errorKey]) {
          // delete the field from the state to show errors
          this.$delete(this.formErrors, errorKey)
          this.$delete(this.invalidFormValues, errorKey)
        }
      })

      if (this.errorKeys.length === 0) {
        this.invalidFormValues = {}
      }
    },
  },
  async mounted() {
    if (!this.isAdding) {
      await this.showData()
    }
  },
  methods: {
    handleProductTypeChange(fullObject) {
      this.updateCategoryInfo(fullObject)
    },
    updateCategoryInfo(productType) {
      const category = productType?.product_category

      if (!category) {
        this.categoryInfo = {
          name: '-',
          minTemperature: '-',
          maxTemperature: '-',
          boxTypes: null,
        }
        return
      }

      this.categoryInfo = {
        name: category.name ?? '-',
        minTemperature: category.min_temperature ?? '-',
        maxTemperature: category.max_temperature ?? '-',
        boxTypes: category.box_types,
      }
    },
    async showData(id) {
      this.isLoadingInitialData = true

      const fetchedData = await ProductApi.show(this.productId ?? id)
      if (fetchedData.errors) {
        this.isLoadingInitialData = false
        return this.$mcToast.error('Terdapat kesalahan format pada input anda')
      }

      const showData = {
        name: fetchedData.data.name,
        product_type_id: fetchedData.data.product_type.id,
        sku: fetchedData.data.sku,
        description: fetchedData.data.description,
        uom: fetchedData.data.uom,
        weight: fetchedData.data.weight,
        volume: fetchedData.data.volume,
        price: fetchedData.data.price,
      }

      this.fetchedProduct = { ...fetchedData.data }
      this.formValues = { ...showData }
      this.productData = { ...showData }

      if (fetchedData.data.product_type) {
        this.productTypeFullObject = fetchedData.data.product_type
        this.updateCategoryInfo(fetchedData.data.product_type)
      }

      this.isLoadingInitialData = false
    },
    filterUnchangedData() {
      const newFormValues = Object.assign(
        {},
        ...Object.keys(this.formValues).map((key) => {
          let currentField = this.productFields.find((item) => item.key == key)
          if (!currentField) currentField = this.uomFields.find((item) => item.key == key)

          const value = this.formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0

            return { [key]: isValid ? Number(value) : null }
          }

          return { [key]: value }
        })
      )

      const newData = { ...this.fieldKeys, ...newFormValues }

      if (this.isSubmitting) {
        this.formValues = Object.assign(
          {},
          ...Object.keys(this.formValues).map((key) => {
            return { [key]: newFormValues[key] }
          })
        )
      }

      const keys = new Set([...Object.keys(newData), ...Object.keys(this.productData)])
      const _newData = {}
      keys.forEach((key) => {
        if (key == 'id') return
        if (this.isAdding && !newData[key]) return
        if (this.productData[key] == newData[key]) return
        _newData[key] = newData[key]
      })

      const entries = Object.entries(_newData)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newData, hasChanged]
    },
    handleCancel() {
      this.formValues = { ...this.productData }
      this.formErrors = {}
      this.invalidFormValues = {}

      if (this.fetchedProduct?.product_type) {
        this.productTypeFullObject = this.fetchedProduct.product_type
        this.updateCategoryInfo(this.fetchedProduct.product_type)
      } else {
        this.productTypeFullObject = null
        this.updateCategoryInfo(null)
      }

      this.$refs['mc-modal-cancel'].hide()
    },
    async handleAccept() {
      if (this.isSubmitting) return this.$mcToast.warning('Mohon tunggu')

      if (this.$refs.formRef.hasErrors)
        return this.$mcToast.error('Terdapat kesalahan format pada input anda')

      this.isSubmitting = true

      const [patchData, hasChanged] = this.filterUnchangedData()

      if (!this.isAdding && !hasChanged) {
        this.isSubmitting = false
        return
      }

      if (this.isAdding) {
        const createdData = await ProductApi.store(this.formValues)
        if (createdData.errors) {
          this.assignErrors(createdData)
          this.isSubmitting = false
          return
        }

        this.isSubmitting = false
        this.$mcToast.success('Data berhasil ditambahkan')
        this.$router.push({ name: 'Master Product Update', params: { id: createdData.data.id } })
        await this.$nextTick()
        await this.showData(createdData.data.id)
      } else {
        const editedData = await ProductApi.update(this.productId, patchData)
        if (editedData.errors) {
          this.assignErrors(editedData)
          this.isSubmitting = false
          return
        }

        this.isSubmitting = false
        this.$mcToast.success('Data berhasil diubah')
        await this.$nextTick()
        await this.showData()
        if (patchData.uom) this.$refs['conversion-table-ref'].getData()
      }

      this.isSubmitting = false
    },
    handleDisableEdit(param) {
      this.isEditingConversion = param
    },
    assignErrors(data) {
      if (data.status === 500) return

      if (data.status === 400) {
        const errorKeys = Object.keys(data.errors)

        // assign to form error
        errorKeys.forEach((errorKey) => {
          const errorMessage =
            apiErrorMappings[errorKey][data.errors[errorKey][0]] ??
            'Terdapat kesalahan format pada input anda'
          this.$set(this.formErrors, errorKey, errorMessage)
        })
      }

      // assign the invalid form values
      this.invalidFormValues = { ...this.formValues }

      this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
  },
}
</script>
